import React from 'react';
import {
  Field,
  PerspectiveType,
  Widget,
  WidgetPayload,
  WidgetType
} from 'src/types';
import RichTextRenderer from '../shared/Perspectives/Renderers/RichTextRenderer';
import TextWidgetInspector from '../AdminExperience/Workflows/ManagePerspective/Inspectors/TextWidgetInspector';
import TableRenderer from '../shared/Perspectives/Renderers/TableRenderer/TableRenderer';
import TableWidgetInspector from '../AdminExperience/Workflows/ManagePerspective/Inspectors/TableWidgetInspector';
import StepperRenderer from '../shared/Perspectives/Renderers/StepperRenderer';
import FormikInput from '../../components/formik/FormikInput';
import BasicFieldWidgetInspector
  from '../AdminExperience/Workflows/ManagePerspective/Inspectors/BasicFieldWidgetInspector';
import AssignRolesRenderer from '../shared/Perspectives/Renderers/AssignRolesRenderer';
import AssignRolesInspector
  from '../AdminExperience/Workflows/ManagePerspective/Inspectors/AssignRolesInspector';
import FormikDatePicker from '../../components/formik/FormikDatePicker';
import {
  CHECKBOX_FIELD,
  CURRENCY_FIELD,
  DATE_FIELD,
  DATETIME_FIELD,
  EMAIL_FIELD,
  FILE_ATTACHMENT_FIELD,
  NUMBER_FIELD,
  SELECT_FIELD,
  TEXT_FIELD,
  URL_FIELD,
  ROLE_ASSIGNMENTS_FIELD, RADIO_FIELD,
} from './fieldConstants';
import {
  CALENDAR,
  STEPPER,
  TABLE,
  WIDGET_TEXT,
  ROLE_ASSIGNMENTS, ACTIVITY_LOG,
} from './perspectiveConstants';
import FormikSelect from '../../components/formik/FormikSelect';
import RoleAssignmentsCard from '../shared/RoleAssignmentsCard';
import Activities from '../shared/Activities';
import FormikYesNo from '../../components/formik/FormikYesNo';
import FormikRadio from '../../components/formik/FormikRadio';
import FormikCheckbox from '../../components/formik/FormikCheckbox';
import FormikUploadFileButton from '../../components/formik/FormikUploadFileButton';
import FormikFilesDropzone from '../../components/formik/FormikMultiFilesDropzone';

export interface InspectorProps {
  // isOpen: boolean,
  // handleClose: () => void,
  workflowId: string,
  selectedWidget: Widget,
  saveWidget: (widgetId: string, widget: WidgetPayload) => Promise<void>,
  deleteWidget: (widgetId: string) => Promise<void>,
  perspectiveType: PerspectiveType,
  selectedWidgetField?: Field,
}

interface WidgetDetails {
  renderer: React.FunctionComponent<any>,
  inspectorComponent: React.FunctionComponent<InspectorProps>,
  label: string,
  droppingItem: {
    i: WidgetType,
    w: number,
    h: number,
  },
  defaultWidget?: Record<string, any>,
}

// eslint-disable-next-line import/prefer-default-export
export const WIDGET_DETAILS_BY_WIDGET_TYPE: Record<WidgetType, WidgetDetails> = {
  [WIDGET_TEXT]: {
    renderer: RichTextRenderer,
    inspectorComponent: TextWidgetInspector,
    label: 'Text',
    droppingItem: {
      i: WIDGET_TEXT,
      w: 3,
      h: 4,
    },
    defaultWidget: {
      text: null,
    },
  },
  [TABLE]: {
    renderer: TableRenderer,
    inspectorComponent: TableWidgetInspector,
    label: 'Table',
    droppingItem: {
      i: TABLE,
      w: 6,
      h: 20,
    },
  },
  [CALENDAR]: {
    renderer: TableRenderer,
    inspectorComponent: TableWidgetInspector,
    label: 'Calendar',
    droppingItem: {
      i: CALENDAR,
      w: 6,
      h: 20,
    },
  },
  [STEPPER]: {
    renderer: StepperRenderer,
    inspectorComponent: () => <div />,
    label: 'Stepper',
    droppingItem: {
      i: STEPPER,
      w: 4,
      h: 20,
    },
  },
  [ROLE_ASSIGNMENTS]: {
    renderer: RoleAssignmentsCard,
    inspectorComponent: () => <div />,
    label: 'Stepper',
    droppingItem: {
      i: ROLE_ASSIGNMENTS,
      w: 4,
      h: 20,
    },
  },
  [ACTIVITY_LOG]: {
    renderer: ({ engagementId }: { engagementId: string}) => <Activities title="Events" url={`/automatic/engagements/${engagementId}/events/`} />,
    inspectorComponent: () => <div />,
    label: 'Stepper',
    droppingItem: {
      i: ACTIVITY_LOG,
      w: 4,
      h: 20,
    },
  },
  [TEXT_FIELD]: {
    renderer: (props: any) => (
      <FormikInput
        fullWidth
        {...props}
      />
    ),
    inspectorComponent: BasicFieldWidgetInspector,
    label: 'Text field',
    droppingItem: {
      i: TEXT_FIELD,
      w: 3,
      h: 4,
    },
  },
  [ROLE_ASSIGNMENTS_FIELD]: {
    renderer: AssignRolesRenderer,
    inspectorComponent: AssignRolesInspector,
    label: 'Role assignments',
    droppingItem: {
      i: ROLE_ASSIGNMENTS_FIELD,
      w: 12,
      h: 12,
    },
  },
  [DATE_FIELD]: {
    renderer: (props: any) => (
      <FormikDatePicker
        fullWidth
        {...props}
      />
    ),
    inspectorComponent: BasicFieldWidgetInspector,
    label: 'Date field',
    droppingItem: {
      i: DATE_FIELD,
      w: 3,
      h: 4,
    },
  },
  [DATETIME_FIELD]: {
    renderer: (props: any) => (
      <FormikDatePicker
        fullWidth
        {...props}
      />
    ),
    inspectorComponent: BasicFieldWidgetInspector,
    label: 'Date & time field',
    droppingItem: {
      i: DATETIME_FIELD,
      w: 3,
      h: 4,
    },
  },
  [CHECKBOX_FIELD]: {
    renderer: (props: any) => (
      <FormikCheckbox
        fullWidth
        {...props}
      />
    ),
    inspectorComponent: BasicFieldWidgetInspector,
    label: 'Checkbox',
    droppingItem: {
      i: CHECKBOX_FIELD,
      w: 3,
      h: 4,
    },
  },
  [CURRENCY_FIELD]: {
    renderer: (props: any) => (
      <FormikInput
        fullWidth
        {...props}
      />
    ),
    inspectorComponent: BasicFieldWidgetInspector,
    label: 'Currency field',
    droppingItem: {
      i: CURRENCY_FIELD,
      w: 3,
      h: 4,
    },
  },
  [NUMBER_FIELD]: {
    renderer: (props: any) => (
      <FormikInput
        fullWidth
        look="number"
        {...props}
      />
    ),
    inspectorComponent: BasicFieldWidgetInspector,
    label: 'Number field',
    droppingItem: {
      i: NUMBER_FIELD,
      w: 3,
      h: 4,
    },
  },
  [EMAIL_FIELD]: {
    renderer: (props: any) => (
      <FormikInput
        fullWidth
        {...props}
      />
    ),
    inspectorComponent: BasicFieldWidgetInspector,
    label: 'Email field',
    droppingItem: {
      i: EMAIL_FIELD,
      w: 3,
      h: 4,
    },
  },
  [URL_FIELD]: {
    renderer: (props: any) => (
      <FormikInput
        fullWidth
        {...props}
      />
    ),
    inspectorComponent: BasicFieldWidgetInspector,
    label: 'URL field',
    droppingItem: {
      i: URL_FIELD,
      w: 3,
      h: 4,
    },
  },
  [SELECT_FIELD]: {
    renderer: (props: any) => (
      <FormikSelect
        fullWidth
        {...props}
        options={props.options.map((option: any) => ({
          label: option,
          value: option
        }))}
      />
    ),
    inspectorComponent: BasicFieldWidgetInspector,
    label: 'Select field',
    droppingItem: {
      i: SELECT_FIELD,
      w: 3,
      h: 4,
    },
  },
  [RADIO_FIELD]: {
    renderer: (props: any) => (
      <FormikRadio
        fullWidth
        {...props}
        options={props.options.map((option: any) => ({
          label: option,
          value: option
        }))}
      />
    ),
    inspectorComponent: BasicFieldWidgetInspector,
    label: 'Select field',
    droppingItem: {
      i: RADIO_FIELD,
      w: 3,
      h: 4,
    },
  },
  [FILE_ATTACHMENT_FIELD]: {
    inspectorComponent: BasicFieldWidgetInspector,
    label: 'File attachment field',
    renderer: (props: any) => (
      <FormikFilesDropzone
        fullWidth
        {...props}
      />
    ),
    droppingItem: {
      i: FILE_ATTACHMENT_FIELD,
      w: 3,
      h: 4,
    },
  },
};
